/**
 * Dynamic breadcrumb labels — a report's name, a chat's title, a creator's
 * name. Each resolver is a hook exported by the owning feature; until a
 * feature lands, its resolver returns undefined and the raw URL segment shows.
 */
import type { CrumbSpec } from '@/app/router/registry';
import { useChatTitleResolver } from '@/features/deepdive';
import { useIndustryLabelResolver, useInfluencerNameResolver } from '@/features/influencers';
import { useQuickSearchQueryResolver } from '@/features/quick-search';
import { useReportNameResolver } from '@/features/reports';

type ResolveKind = Extract<CrumbSpec, { kind: 'param' }>['resolve'];
type Resolver = (value: string) => string | undefined;

const RESOLVERS: Record<ResolveKind, () => Resolver> = {
  report: useReportNameResolver,
  chat: useChatTitleResolver,
  influencer: useInfluencerNameResolver,
  industry: useIndustryLabelResolver,
  'quick-search': useQuickSearchQueryResolver,
};

/**
 * Called by the top bar, which is keyed by route id — so the number and order
 * of hooks called here is fixed for the life of a mount.
 */
export function useCrumbLabels(
  specs: readonly CrumbSpec[],
  params: Readonly<Record<string, string | undefined>>,
): string[] {
  return specs.map((spec) => {
    if (spec.kind === 'static') return spec.label;
    const resolve = RESOLVERS[spec.resolve]();
    const value = params[spec.param] ?? '';
    return resolve(value) ?? value;
  });
}
